/**
 * Alle weergavetijden zijn Europe/Amsterdam; alles wat naar de server gaat is UTC-ISO.
 * Kalenderdata (`routeDate`, `redeliveryPlannedFor`) zijn lokale dagen zonder tijd.
 */

export const TIME_ZONE = 'Europe/Amsterdam';

const clockFormatter = new Intl.DateTimeFormat('nl-NL', {
  timeZone: TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const dayFormatter = new Intl.DateTimeFormat('nl-NL', {
  timeZone: TIME_ZONE,
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});

const isoDayFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

function toDate(value: string | number | Date): Date | null {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** `HH:mm` in Europe/Amsterdam; lege string als er niets te tonen valt. */
export function formatClock(value: string | number | Date | null | undefined): string {
  if (value === null || value === undefined) return '';
  const date = toDate(value);
  return date ? clockFormatter.format(date) : '';
}

function capitalise(text: string): string {
  return text.length === 0 ? text : text[0]!.toUpperCase() + text.slice(1);
}

/** "Woensdag 3 september" — de dagkop van scherm C. */
export function formatDayHeading(dateOnly: string): string {
  const date = toDate(`${dateOnly}T12:00:00Z`);
  return date ? capitalise(dayFormatter.format(date)) : '';
}

/** "woensdag 3 september" — zelfde datum, kleine letter, voor midden in een zin. */
export function formatDateInSentence(dateOnly: string): string {
  const date = toDate(`${dateOnly}T12:00:00Z`);
  return date ? dayFormatter.format(date) : '';
}

/** Lokale kalenderdag (`yyyy-mm-dd`) van een moment. */
export function localDateOf(value: string | number | Date = Date.now()): string {
  const date = toDate(value) ?? new Date();
  return isoDayFormatter.format(date);
}

/** De lokale kalenderdag ná `reference`. */
export function tomorrowLocalDate(reference: string | number | Date = Date.now()): string {
  const date = toDate(reference) ?? new Date();
  return localDateOf(new Date(date.getTime() + 24 * 60 * 60 * 1000));
}

export function isTomorrow(
  dateOnly: string | null | undefined,
  reference: string | number | Date = Date.now(),
): boolean {
  return !!dateOnly && dateOnly === tomorrowLocalDate(reference);
}

/** "96,4 km" — Nederlandse notatie, één decimaal. */
export function formatKm(meters: number | null | undefined): string {
  if (meters === null || meters === undefined || !Number.isFinite(meters)) return '';
  return `${(meters / 1000).toLocaleString('nl-NL', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} km`;
}

/** "44 km" — heel, voor de terugrit. */
export function formatKmWhole(meters: number | null | undefined): string {
  if (meters === null || meters === undefined || !Number.isFinite(meters)) return '';
  return `${Math.round(meters / 1000).toLocaleString('nl-NL')} km`;
}

/** "3:05 u" — rijtijd. */
export function formatDrivingTime(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return '';
  const total = Math.max(0, Math.round(seconds / 60));
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  return `${hours}:${String(minutes).padStart(2, '0')} u`;
}

/** "38 min" — duur van de terugrit. */
export function formatMinutes(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return '';
  return `${Math.max(0, Math.round(seconds / 60))} min`;
}

/** Uitloop in hele minuten ten opzichte van de planning; `null` als er niets af te wijken valt. */
export function delayMinutes(
  plannedArrivalAt: string | null,
  reference: string | number | Date = Date.now(),
): number | null {
  if (!plannedArrivalAt) return null;
  const planned = toDate(plannedArrivalAt);
  const now = toDate(reference);
  if (!planned || !now) return null;
  const minutes = Math.round((now.getTime() - planned.getTime()) / 60000);
  return minutes > 0 ? minutes : null;
}

export function nowIso(): string {
  return new Date().toISOString();
}
